import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, DateDecoder, EnumDecoder, field, IntegerDecoder, MapDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { Formatter } from '@stamhoofd/utility';
import { Image } from './files/Image';
import { GroupGenderType } from './GroupGenderType';
import { GroupPrices } from './GroupPrices';
export var WaitingListType;
(function (WaitingListType) {
    WaitingListType["None"] = "None";
    WaitingListType["PreRegistrations"] = "PreRegistrations";
    WaitingListType["ExistingMembersFirst"] = "ExistingMembersFirst";
    WaitingListType["All"] = "All";
})(WaitingListType || (WaitingListType = {}));
export class CycleInformation extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.startDate = null;
        this.endDate = null;
        this.registeredMembers = 0;
        /**
         * Amount of members that is reserved (e.g in payment process, or a member on the waiting list that is invited)
         */
        this.reservedMembers = 0;
        /**
         * Amount of members on the waiting list
         */
        this.waitingListSize = 0;
    }
    get dateRangeDescription() {
        if (!this.endDate) {
            return null;
        }
        if (!this.startDate) {
            return null;
        }
        const daysBetween = Math.abs(this.endDate.getTime() - this.startDate.getTime()) / (1000 * 3600 * 24);
        if (Formatter.dateWithoutDay(this.startDate) === Formatter.dateWithoutDay(this.endDate)) {
            return `${Formatter.dateWithoutDay(this.startDate)}`;
        }
        if (daysBetween < 10 * 30) {
            return `${Formatter.dateWithoutDay(this.startDate)} - ${Formatter.dateWithoutDay(this.endDate)}`;
        }
        const year1 = Formatter.year(this.startDate);
        const year2 = Formatter.year(this.endDate);
        if (year1 !== year2) {
            return `${year1} - ${year2}`;
        }
        return `${year1}`;
    }
}
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], CycleInformation.prototype, "startDate", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], CycleInformation.prototype, "endDate", void 0);
__decorate([
    field({
        decoder: IntegerDecoder,
        nullable: true
    })
], CycleInformation.prototype, "registeredMembers", void 0);
__decorate([
    field({
        decoder: IntegerDecoder,
        nullable: true,
        version: 139
    })
], CycleInformation.prototype, "reservedMembers", void 0);
__decorate([
    field({
        decoder: IntegerDecoder,
        nullable: true,
        version: 139
    })
], CycleInformation.prototype, "waitingListSize", void 0);
export class GroupSettings extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.name = "";
        this.description = "";
        /**
         * Information about previous cycles
         */
        this.cycleSettings = new Map();
        /**
         * Dispay start and end date times
         */
        this.displayStartEndTime = false;
        this.registrationStartDate = null;
        this.registrationEndDate = null;
        this.prices = [];
        this.genderType = GroupGenderType.Mixed;
        this.minAge = null;
        this.maxAge = null;
        this.waitingListType = WaitingListType.None;
        /**
         * Only used for waitingListType = PreRegistrations
         */
        this.preRegistrationsDate = null;
        this.maxMembers = null;
        this.waitingListIfFull = true;
        /**
         * If maxMembers is not null, this will get filled in by the backend
         */
        this.registeredMembers = 0;
        /**
         * Amount of members that is reserved (e.g in payment process, or a member on the waiting list that is invited)
         */
        this.reservedMembers = 0;
        /**
         * Amount of members on the waiting list
         */
        this.waitingListSize = null;
        /**
         * Of er voorrang moet gegeven worden aan broers en/of zussen als er wachtlijsten of voorinschrijvingen zijn
         */
        this.priorityForFamily = true;
        /**
         * @deprecated
         */
        this.images = [];
        this.coverPhoto = null;
        this.squarePhoto = null;
        this.location = "";
        /**
         * Require that the member is already registered for one of these groups before allowing to register for this group.
         * If it is empty, then it is not enforced
         */
        this.requireGroupIds = [];
        /**
         * Require that the member is already registered for one of these groups before allowing to register for this group.
         * If it is empty, then it is not enforced
         */
        this.requirePreviousGroupIds = [];
        this.preventPreviousGroupIds = [];
    }
    get isFull() {
        var _a;
        return this.maxMembers !== null && this.registeredMembers !== null && (this.registeredMembers + ((_a = this.reservedMembers) !== null && _a !== void 0 ? _a : 0)) >= this.maxMembers;
    }
    get canHaveWaitingList() {
        return (this.waitingListType !== WaitingListType.None && this.waitingListType !== WaitingListType.PreRegistrations) || (this.waitingListIfFull && this.isFull);
    }
    get canHaveWaitingListWithoutMax() {
        return (this.waitingListType !== WaitingListType.None && this.waitingListType !== WaitingListType.PreRegistrations);
    }
    get availableMembers() {
        var _a;
        if (this.maxMembers === null) {
            return null;
        }
        if (this.registeredMembers === null) {
            return null;
        }
        return this.maxMembers - this.registeredMembers - ((_a = this.reservedMembers) !== null && _a !== void 0 ? _a : 0);
    }
    getGroupPrices(date) {
        let foundPrice = undefined;
        // Determine price
        for (const price of this.prices) {
            if (!price.startDate || price.startDate <= date) {
                if (foundPrice && foundPrice.startDate && (!price.startDate || price.startDate < foundPrice.startDate)) {
                    continue;
                }
                foundPrice = price;
            }
        }
        return foundPrice;
    }
    get maxYear() {
        if (this.minAge === null) {
            return null;
        }
        return (this.startDate.getFullYear() - this.minAge);
    }
    get minYear() {
        if (this.maxAge === null) {
            return null;
        }
        return (this.startDate.getFullYear() - this.maxAge);
    }
    get forAdults() {
        return ((this.minAge && this.minAge >= 18) || (this.maxAge && this.maxAge > 18));
    }
    getAgeGenderDescription({ includeAge = false, includeGender = false } = {}) {
        let who = '';
        if (includeAge && this.minYear && this.maxYear) {
            if (includeGender && this.genderType === GroupGenderType.OnlyMale) {
                if (this.forAdults) {
                    who += "mannen geboren in";
                }
                else {
                    who += "jongens geboren in";
                }
            }
            else if (includeGender && this.genderType === GroupGenderType.OnlyFemale) {
                if (this.forAdults) {
                    who += "vrouwen geboren in";
                }
                else {
                    who += "meisjes geboren in";
                }
            }
            else {
                who += "geboren in";
            }
            who += " " + (this.minYear) + " - " + (this.maxYear);
        }
        else if (includeAge && this.maxYear) {
            if (includeGender && this.genderType === GroupGenderType.OnlyMale) {
                if (this.forAdults) {
                    who += "mannen geboren in of voor";
                }
                else {
                    who += "jongens geboren in of voor";
                }
            }
            else if (includeGender && this.genderType === GroupGenderType.OnlyFemale) {
                if (this.forAdults) {
                    who += "vrouwen geboren in of voor";
                }
                else {
                    who += "meisjes geboren in of voor";
                }
            }
            else {
                who += "geboren in of voor";
            }
            who += " " + (this.maxYear);
        }
        else if (includeAge && this.minYear) {
            if (includeGender && this.genderType === GroupGenderType.OnlyMale) {
                if (this.forAdults) {
                    who += "mannen geboren in of na";
                }
                else {
                    who += "jongens geboren in of na";
                }
            }
            else if (includeGender && this.genderType === GroupGenderType.OnlyFemale) {
                if (this.forAdults) {
                    who += "vrouwen geboren in of na";
                }
                else {
                    who += "meisjes geboren in of na";
                }
            }
            else {
                who += "geboren in of na";
            }
            who += " " + (this.minYear);
        }
        else if (includeGender) {
            if (this.genderType === GroupGenderType.OnlyMale) {
                if (this.forAdults) {
                    who += "mannen";
                }
                else {
                    who += "jongens";
                }
            }
            else if (this.genderType === GroupGenderType.OnlyFemale) {
                if (this.forAdults) {
                    who += "vrouwen";
                }
                else {
                    who += "meisjes";
                }
            }
        }
        if (!who) {
            return null;
        }
        return who;
    }
    getMemberCount({ cycle, waitingList }) {
        let data = this;
        if (cycle !== undefined) {
            data = this.cycleSettings.get(cycle);
        }
        if (!data) {
            return null;
        }
        if (waitingList) {
            return data.waitingListSize;
        }
        return data.registeredMembers;
    }
    getStartDate({ cycle }) {
        let data = this;
        if (cycle !== undefined) {
            data = this.cycleSettings.get(cycle);
        }
        if (!data) {
            return null;
        }
        return data.startDate;
    }
    getEndDate({ cycle }) {
        let data = this;
        if (cycle !== undefined) {
            data = this.cycleSettings.get(cycle);
        }
        if (!data) {
            return null;
        }
        return data.endDate;
    }
    getShortCode(maxLength) {
        return Formatter.firstLetters(this.name, maxLength);
    }
    get dateRangeDescription() {
        const daysBetween = Math.abs(this.endDate.getTime() - this.startDate.getTime()) / (1000 * 3600 * 24);
        if (Formatter.dateWithoutDay(this.startDate) === Formatter.dateWithoutDay(this.endDate)) {
            return `${Formatter.dateWithoutDay(this.startDate)}`;
        }
        if (daysBetween < 10 * 30) {
            return `${Formatter.dateWithoutDay(this.startDate)} - ${Formatter.dateWithoutDay(this.endDate)}`;
        }
        const year1 = Formatter.year(this.startDate);
        const year2 = Formatter.year(this.endDate);
        if (year1 !== year2) {
            return `${year1} - ${year2}`;
        }
        return `${year1}`;
    }
    getEstimatedTimeRange(cycleOffset = 0) {
        const yearStart = Formatter.year(this.startDate) - cycleOffset;
        const yearEnd = Formatter.year(this.endDate) - cycleOffset;
        if (yearStart !== yearEnd) {
            return `${yearStart} - ${yearEnd}`;
        }
        return `${yearStart}`;
    }
}
__decorate([
    field({ decoder: StringDecoder })
], GroupSettings.prototype, "name", void 0);
__decorate([
    field({ decoder: StringDecoder })
], GroupSettings.prototype, "description", void 0);
__decorate([
    field({ decoder: new MapDecoder(IntegerDecoder, CycleInformation), version: 193 })
], GroupSettings.prototype, "cycleSettings", void 0);
__decorate([
    field({ decoder: DateDecoder }),
    field({ decoder: DateDecoder, version: 33, upgrade: (d) => {
            const d2 = new Date(d);
            d2.setUTCHours(-2, 0, 0, 0); // brussels time
            return d2;
        } })
], GroupSettings.prototype, "startDate", void 0);
__decorate([
    field({ decoder: DateDecoder }),
    field({ decoder: DateDecoder, version: 33, upgrade: (d) => {
            const d2 = new Date(d);
            d2.setUTCHours(23 - 2, 59, 0, 0); // brussels time
            return d2;
        } })
], GroupSettings.prototype, "endDate", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 78 })
], GroupSettings.prototype, "displayStartEndTime", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: false, version: 73, upgrade: function () { return this.startDate; } }),
    field({ decoder: DateDecoder, nullable: true, version: 192, downgrade: function () { var _a; return (_a = this.registrationStartDate) !== null && _a !== void 0 ? _a : this.startDate; } })
], GroupSettings.prototype, "registrationStartDate", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: false, version: 73, upgrade: function () { return this.endDate; } }),
    field({ decoder: DateDecoder, nullable: true, version: 192, downgrade: function () { var _a; return (_a = this.registrationEndDate) !== null && _a !== void 0 ? _a : this.endDate; } })
], GroupSettings.prototype, "registrationEndDate", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(GroupPrices) })
], GroupSettings.prototype, "prices", void 0);
__decorate([
    field({ decoder: new EnumDecoder(GroupGenderType) })
], GroupSettings.prototype, "genderType", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, field: "maxBirthYear" }),
    field({ decoder: IntegerDecoder, nullable: true, version: 12, upgrade: (year) => {
            if (year === null) {
                return null;
            }
            return 2020 - year;
        } })
], GroupSettings.prototype, "minAge", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, field: "minBirthYear" }),
    field({
        decoder: IntegerDecoder, nullable: true, version: 12, upgrade: (year) => {
            if (year === null) {
                return null;
            }
            return 2020 - year;
        }
    })
], GroupSettings.prototype, "maxAge", void 0);
__decorate([
    field({ decoder: new EnumDecoder(WaitingListType), version: 16 })
], GroupSettings.prototype, "waitingListType", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true, version: 16 })
], GroupSettings.prototype, "preRegistrationsDate", void 0);
__decorate([
    field({
        decoder: IntegerDecoder, nullable: true, version: 16,
    }),
    field({
        decoder: IntegerDecoder,
        nullable: true,
        version: 74,
        upgrade: function (old) {
            // Clear value if waiting list type is none
            if (this.waitingListType !== WaitingListType.None) {
                return old;
            }
            return null;
        }
    })
], GroupSettings.prototype, "maxMembers", void 0);
__decorate([
    field({
        decoder: BooleanDecoder, version: 79,
    })
], GroupSettings.prototype, "waitingListIfFull", void 0);
__decorate([
    field({
        decoder: IntegerDecoder,
        nullable: true,
        version: 77
    })
], GroupSettings.prototype, "registeredMembers", void 0);
__decorate([
    field({
        decoder: IntegerDecoder,
        nullable: true,
        version: 139
    })
], GroupSettings.prototype, "reservedMembers", void 0);
__decorate([
    field({
        decoder: IntegerDecoder,
        nullable: true,
        version: 139
    })
], GroupSettings.prototype, "waitingListSize", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 16 })
], GroupSettings.prototype, "priorityForFamily", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(Image), version: 58 })
], GroupSettings.prototype, "images", void 0);
__decorate([
    field({ decoder: Image, nullable: true, version: 65 })
], GroupSettings.prototype, "coverPhoto", void 0);
__decorate([
    field({ decoder: Image, nullable: true, version: 66 })
], GroupSettings.prototype, "squarePhoto", void 0);
__decorate([
    field({ decoder: StringDecoder, version: 76 })
], GroupSettings.prototype, "location", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(StringDecoder), version: 83 })
], GroupSettings.prototype, "requireGroupIds", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(StringDecoder), version: 100 })
], GroupSettings.prototype, "requirePreviousGroupIds", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(StringDecoder), version: 102 })
], GroupSettings.prototype, "preventPreviousGroupIds", void 0);
export const GroupSettingsPatch = GroupSettings.patchType();
//# sourceMappingURL=GroupSettings.js.map